import { useQuery } from "@tanstack/react-query";
import removeNullFields from "@helper/removeNullFields";
import { mapRegionAndStateToRecord, useRegionsStatesMap } from "./useRegions";
import { useMemo } from "react";
import { queryClient } from "@contexts/QueryClientContext";

const API_URL = '/api/dashboardapi/load-shortlisted-suppliers';

export type SupplierType = {
    supplierName: string;
    supplierImage: string;
    supplierSlug: string;
    averageReviewRating: number;
    reviewCount: number;
    regionId: number;
    priceTier?: 1 | 2 | 3 | 4;
    itemType: number;
    weddingPlannerId?: number;
    price?: number;
    note?: string;
    contactPerson?: string;
    phone?: string;
    email?: string;
    supplierId: number;
    supplierCategoryId: number;
    maxGuest?: number;
    minGuest?: number;
    isPpa?: boolean | null;
};

export type SupplierTypeExtended = SupplierType & {
    supplierUrl: string;
    categoryName: string;
    categorySlug: string;
    regionName: string;
}

type WithEnquiryMessagingType = {
    enquiryMessage: EnquiryMessageType;
    enquiryDate?: string;
    enquryText?: string;
    enquiryState: EnquiryMessagingStatusType;
}

export type SupplierWithEnquiryMessagingType = SupplierType & WithEnquiryMessagingType;

type EnquiryMessageType = {
    id: number;
    supplierId: number;
    lastMessage?: string;
    lastMessageDate?: string;
    isLegacy?: boolean;
}

export type SupplierTypeWithRegion = SupplierType & {
    region: {
        id: number;
        name: string;
        slug: string;
    };
    state: {
        id: number;
        name: string;
    };
};

type EnquiryMessagingStatusType = 'not-contacted' | 'contacted' | 'legacy';

function getEnquiryState(supplier: SupplierWithEnquiryMessagingType) {
    if (!supplier.enquiryMessage) {
        return 'not-contacted';
    }
    if(supplier.enquiryMessage?.isLegacy) {
        return 'legacy';
    }
    if(supplier.enquiryMessage?.id) {
        return 'contacted';
    }
    return 'not-contacted';
}

export const fetchAllContactedAndSaved = async <T extends boolean>(
    categoryId: number, 
    withImages: boolean = true, 
    withMessage: T = false as T
): Promise<T extends true ? SupplierWithEnquiryMessagingType[] : SupplierType[]> => {
    const queryString = new URLSearchParams({
        categoryId: categoryId.toString(),
        noimages: (!withImages).toString(),
        withMessage: withMessage.toString(),
    }).toString();
    const response = await fetch(`${API_URL}?${queryString}`);
    if (!response.ok) {
        throw new Error('Network response was not ok');
    }
    const data = await response.json();
    return data.map((entity: any) => removeNullFields({
        ...entity,
        ...(entity.priceTier ? {priceTier: entity.priceTier as 1 | 2 | 3 | 4} : {}),
    })) as T extends true ? SupplierWithEnquiryMessagingType[] : SupplierType[];
}

export const QUERY_KEY_ALL_CONTACTED_AND_SAVED = ['all-contacted-and-saved'];

export const useAllContactedAndSaved = (categoryId: number, withImages: boolean = true, withMessage: boolean = false, options = {}) => {
    return useQuery({
        queryKey: [...QUERY_KEY_ALL_CONTACTED_AND_SAVED, categoryId, withImages, withMessage],
        queryFn: ({ queryKey }) => {
            const [_key, categoryId, withImages, withMessage] = queryKey as [string, number, boolean, boolean];
            return fetchAllContactedAndSaved(categoryId, withImages, withMessage)
        },
        select: (data) => {
            return data.map((entity: any) => ({
                ...entity,
                ...(withMessage ? {enquiryState: getEnquiryState(entity)} : {}),
            }));
        },
        ...options,
    });
}

export const useInteractedSuppliers = (categoryId: number, withImages: boolean = true, withMessage: boolean = false) => {
    const { data, ...bookingsQuery } = useAllContactedAndSaved(categoryId, withImages, withMessage);
    const { regionsMap, statesMap, ...regionsQuery } = useRegionsStatesMap();
    const regionMapper = (regionsMap && statesMap) 
        ?( withMessage ? mapRegionAndStateToRecord<SupplierWithEnquiryMessagingType>(regionsMap, statesMap) : mapRegionAndStateToRecord<SupplierType>(regionsMap, statesMap))
        : null;
    // @ ts-expect-error IDK how to fix this error of polymorh types
    const enrichedWithRegionAndState = useMemo(() => regionMapper ? data?.map(regionMapper) : null, [data, regionMapper]);
    return {
        bookingsQuery: {
            ...bookingsQuery,
        },
        regionsQuery: {
            ...regionsQuery,
        },
        data: enrichedWithRegionAndState,
    }
}


export function isFavorite(itemType: number) {
    return itemType === 2 || itemType === 3;
}

export function isContacted(itemType: number) {
    return itemType === 1 || itemType === 3;
}


export const updateContacted = (categoryId: number, supplierId: number) => {
    queryClient.setQueryData([...QUERY_KEY_ALL_CONTACTED_AND_SAVED, categoryId, true, true], (suppliers: SupplierType[]) => suppliers ? [
        ...suppliers.map((c: SupplierType) => {
            if(c.supplierCategoryId === categoryId && c.supplierId === supplierId) {
                return {
                    ...c,
                    itemType: c.itemType == 2 ? 3 : 1,
                }
            }
            return c;
        })
    ] : suppliers);
    queryClient.setQueryData([...QUERY_KEY_ALL_CONTACTED_AND_SAVED, categoryId, true, false], (suppliers: SupplierType[]) => suppliers ? [
        ...suppliers.map((c: SupplierType) => {
            if(c.supplierCategoryId === categoryId && c.supplierId === supplierId) {
                return {
                    ...c,
                    itemType: c.itemType == 2 ? 3 : 1,
                }
            }
            return c;
        })
    ] : suppliers);
}

export const updateFavorites = (categoryId: number, supplierId: number, isFavorite: boolean) => {
    queryClient.setQueryData([...QUERY_KEY_ALL_CONTACTED_AND_SAVED, categoryId, true], (suppliers: SupplierType[]) => [
        ...suppliers.map((c: SupplierType) => {
            if(c.supplierCategoryId === categoryId && c.supplierId === supplierId) {
                return {
                    ...c,
                    itemType: isFavorite ? c.itemType == 1 ? 3 : 2 : c.itemType == 3 ? 1 : 0,
                }
            }
            return c;
        })
    ]);
    queryClient.setQueryData([...QUERY_KEY_ALL_CONTACTED_AND_SAVED, categoryId, true, false], (suppliers: SupplierType[]) => suppliers ? [
        ...suppliers.map((c: SupplierType) => {
            if(c.supplierCategoryId === categoryId && c.supplierId === supplierId) {
                return {
                    ...c,
                    itemType: isFavorite ? c.itemType == 1 ? 3 : 2 : c.itemType == 3 ? 1 : 0,
                }
            }
            return c;
        })
    ] : suppliers);
}